import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FiInfo } from "react-icons/fi"

import useT from "utils/hooks/useT"
import Loading from "components/Loading"
import { Popup, ConfigContainer } from "components"
import { Container, Title, Text } from "components/Form"
import SpecEditor from "components/Lims/SpecEditor"
import SpecChangeControl from "components/Config/AnalysisConfigurationPage/SpecChangeControl"
import { message, limsSpecs, limsSpecVersions } from "state_management"

// ONE analysis's parameter specification (SPEC-063, Plannertech-user#4149) — opened from the
// Analysis-specifications register.
//
// It reuses `components/Lims/SpecEditor` UNCHANGED. The editor was never the problem; its location
// was. A copy would have drifted from the original within a release (the lesson of RR8
// #4063/#4068), and the calculated-parameter engine, the reference validation and the preview all
// hang off it.
//
// Two behaviours are carried over deliberately from the definition page it replaces:
//
//  * **Seed from the PENDING version when there is one** (the #4092 lesson). A saved change becomes
//    a draft and leaves the effective specification in force, so seeding from the effective one
//    would show the author their own change back as if it had been thrown away. The seed therefore
//    WAITS for the version fetch instead of seeding from the effective spec and correcting after —
//    which would overwrite whatever the user had already typed.
//  * **A change to an APPROVED specification demands a stated reason**, asked in a popup at save
//    time (the #4073/#4074 shape), audited server-side. The FIRST specification on an analysis that
//    has none is its approved baseline, not a change, and needs no reason.
//
// `canAuthor` false (any LIMS user who is not the planner) renders the same screen read-only: the
// save bar is gone and the body is `inert`, so a reviewer can read what they are about to sign
// without being able to type into it. The server refuses the write regardless.
export default function AnalysisSpecificationEditor({ analysisDefinitionId, canAuthor, onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const row = useSelector((state) => limsSpecs.selectSpecRow(state, analysisDefinitionId))
  const pending = useSelector((state) => limsSpecVersions.selectPendingVersion(state, analysisDefinitionId))

  const [parameters, setParameters] = useState([])
  const [specValid, setSpecValid] = useState(true)
  const [reasonPrompt, setReasonPrompt] = useState(false)
  const [changeReason, setChangeReason] = useState("")
  const [seeded, setSeeded] = useState(false)

  // The spec-level `formula` is a legacy field: nothing edits it any more, but it is part of the
  // versioned content, so it is carried through the save untouched rather than silently cleared.
  const formulaRef = useRef("")
  // What the editor was seeded WITH, so "did this actually change?" is judged against the same
  // source it was filled from — reopening an analysis with a pending draft must not read as a
  // fresh change (which would demand a reason and create a second draft on save).
  const initialRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    const seed = (pendingVersion) => {
      if (cancelled) return
      const source = pendingVersion || row || {}
      const seedParameters = limsSpecs.normalizeParameters(source.parameters)
      formulaRef.current = source.formula || ""
      initialRef.current = JSON.stringify(seedParameters)
      setParameters(seedParameters)
      setSeeded(true)
    }
    dispatch(limsSpecVersions.fetch(analysisDefinitionId)).then(
      (items) => seed(limsSpecVersions.pendingVersionOf(items)),
      // A failed version fetch must fall back to the effective spec, never leave the editor blank.
      () => seed(null)
    )
    return () => {
      cancelled = true
    }
  }, [dispatch, analysisDefinitionId]) // eslint-disable-line react-hooks/exhaustive-deps

  const changed = () =>
    initialRef.current !== null && JSON.stringify(limsSpecs.normalizeParameters(parameters)) !== initialRef.current

  // A reason is required only when an APPROVED specification is being changed. `row` carries the
  // effective version number, which is exactly "there is something approved to supersede".
  const needsReason = () => Boolean(row && row.effectiveVersionNumber && changed())

  const persist = (reason) => {
    dispatch(limsSpecs.save(analysisDefinitionId, { parameters, formula: formulaRef.current }, reason))
      .then(() => {
        dispatch(message.success(t(needsReasonAtSave(row) ? "limsAnalysisSpecSavedDraft" : "limsAnalysisSpecSaved")))
        // Refresh the register so its status column reflects the save immediately, then step back
        // to it: "awaiting review" on the row it came from is the clearest possible answer to
        // "where did my change go?".
        dispatch(limsSpecs.fetch())
        onClose()
      })
      .catch(() => dispatch(message.error(t("limsAnalysisSpecSaveFailed"))))
  }

  const handleSave = () => {
    if (!specValid) {
      dispatch(message.warning(t("limsCalcFormulaInvalidSave")))
      return
    }
    if (!changed()) {
      onClose()
      return
    }
    if (needsReason()) {
      setReasonPrompt(true)
      return
    }
    persist(null)
  }

  if (!seeded) {
    return (
      <Popup onCancel={onClose}>
        <div className="LimsAnalysisSpecEditor">
          <Loading />
        </div>
      </Popup>
    )
  }

  return (
    <>
      <Popup onCancel={onClose}>
        {/* Deliberately NOT `nonScrollable`: a long parameter list plus the version history is
            taller than the viewport, and the popup is what scrolls it. */}
        <div className="LimsAnalysisSpecEditor">
          {/* The whole answer to "it didn't save". The change IS saved — as a version waiting for two
              signatures before it becomes the specification result entry judges against. */}
          {pending && (
            <p className="LimsAnalysisSpecEditor-pending">
              <FiInfo />
              <span>
                {t(
                  pending.state === "reviewer-approved" ? "limsSpecDraftPendingSupervisor" : "limsSpecDraftPending",
                  { n: pending.versionNumber }
                )}
              </span>
            </p>
          )}
          {!canAuthor && (
            <p className="LimsAnalysisSpecEditor-readonly">
              <FiInfo /> <span>{t("limsAnalysisSpecReadOnly")}</span>
            </p>
          )}

          {/* The modal says WHICH analysis it is editing — that is what the page heading used to
              carry, and a modal over a register has to name its row. */}
          <ConfigContainer
            title={(row && row.name) || t("limsSpecification")}
            disabled={!canAuthor}
            cancelHandler={canAuthor ? onClose : undefined}
            submitHandler={canAuthor ? handleSave : undefined}
          >
            <div className="LimsAnalysisSpecEditor-body" inert={!canAuthor ? "" : undefined}>
              <SpecEditor
                parameters={parameters}
                onParametersChange={setParameters}
                onValidityChange={setSpecValid}
              />
            </div>
          </ConfigContainer>

          <hr />
          <p className="Pheading ConfigurationPage">{t("limsSpecVersions")}</p>
          <div className="ConfigSection-body">
            <SpecChangeControl analysisDefinitionId={analysisDefinitionId} />
          </div>
        </div>
      </Popup>

      {/* The reason for changing an approved specification, asked next to the action that needs it.
          Mandatory — the save is audited against it server-side, so there is no "save anyway". */}
      {reasonPrompt && (
        <Popup nonScrollable onCancel={() => setReasonPrompt(false)}>
          <ConfigContainer
            title={t("limsSpecChangeReason")}
            cancelHandler={() => setReasonPrompt(false)}
            submitHandler={() => {
              if (!changeReason.trim()) {
                dispatch(message.warning(t("limsSpecChangeReasonRequired")))
                return
              }
              setReasonPrompt(false)
              persist(changeReason)
            }}
          >
            <Container>
              <Title>{t("limsSpecChangeReasonHelp")}</Title>
              <Text value={changeReason} onChange={setChangeReason} />
            </Container>
          </ConfigContainer>
        </Popup>
      )}
    </>
  )
}

// Whether THIS save lands as a draft awaiting approval (an approved specification was changed)
// rather than taking effect at once (the analysis had none). Drives which confirmation is shown,
// so the message never promises that a change is live when it is waiting for two signatures.
function needsReasonAtSave(row) {
  return Boolean(row && row.effectiveVersionNumber)
}
